import { useState, useEffect, useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

import useUpdateEffect from 'hooks/useUpdateEffect';

import {
  sanitizeDefaultValues,
  parseLocationSearch,
  generateObjectFromSearch,
} from './utils';
import locationController from './LocationController';

interface UseStorageOptions {
  persistOnUrl?: boolean;
  typeCoercion?: boolean;
  initialQueryString?: string;
  persistOnDB?: boolean;
  persistFalsy?: boolean;
  storageObject?: Storage;
}

export default function useStorage<T>(
  key: string,
  defaultValue: T,
  {
    persistOnUrl = false,
    typeCoercion = false,
    initialQueryString,
    persistOnDB = false,
    persistFalsy = false,
    storageObject = localStorage,
  }: UseStorageOptions = {},
) {
  const [value, setValue] = useState<T>(() => {
    const searchValues = parseLocationSearch({
      typeCoercion,
      queryString: undefined,
    });

    const persistedQueryValues = persistOnUrl
      ? generateObjectFromSearch(searchValues, defaultValue)
      : {};

    const backendQueryValues =
      initialQueryString && isEmpty(searchValues)
        ? generateObjectFromSearch(
            parseLocationSearch({
              typeCoercion,
              queryString: initialQueryString,
            }),
            defaultValue,
          )
        : {};

    let valueToPersist;
    if (!isEmpty(searchValues)) {
      valueToPersist = persistedQueryValues;
    } else if (!isEmpty(backendQueryValues)) {
      valueToPersist = backendQueryValues;
    } else {
      valueToPersist = {};
    }

    return { ...defaultValue, ...valueToPersist };
  });

  const valuesToRegister = useMemo(
    () =>
      value
        ? sanitizeDefaultValues(value, defaultValue)
        : /* istanbul ignore next -- @preserve */ {},
    [value, defaultValue],
  );

  useEffect(() => {
    locationController.register(key, defaultValue, valuesToRegister);

    return () => locationController.unregister(key);
  }, [key, valuesToRegister, defaultValue]);

  useUpdateEffect(() => {
    locationController.replaceLocationState();
  }, [valuesToRegister, key]);

  const updateStorage = useCallback(() => {
    if (!persistOnDB) return;

    /* istanbul ignore next -- @preserve */
    if (value === undefined && !persistFalsy) {
      storageObject.removeItem(key);
      return;
    }
    storageObject.setItem(key, JSON.stringify(value));
  }, [key, persistFalsy, storageObject, value, persistOnDB]);

  useEffect(() => {
    updateStorage();
  }, [updateStorage]);

  // Listen to browser back/forward button clicks
  useEffect(() => {
    const handlePopState = () => {
      const searchValues = parseLocationSearch({ typeCoercion });
      const persistedQueryValues = persistOnUrl
        ? generateObjectFromSearch(searchValues, defaultValue)
        : {};

      const persistedDbJson = persistOnDB
        ? storageObject.getItem(key)
        : undefined;

      const persistedDbValues = persistedDbJson
        ? JSON.parse(persistedDbJson)
        : {};

      const valueToPersist = isEmpty(searchValues)
        ? persistedDbValues
        : persistedQueryValues;

      setValue({ ...defaultValue, ...valueToPersist });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    key,
    defaultValue,
    persistOnDB,
    persistOnUrl,
    storageObject,
    typeCoercion,
  ]);

  const reset = useCallback(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  return [value, setValue, reset] as const;
}
